import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	getActions,
	getAllActions,
	createAction,
	updateAction,
	deleteAction,
} from "../services/action.service";

// Re-export types for convenience
export type {
	ActionRequest,
	ActionUpdateRequest,
} from "../services/action.service";

export const useGetActions = (
	search?: string,
	page?: number,
	pageSize?: number,
	type?: string,
) => {
	return useQuery({
		queryKey: ["actions", search, page, pageSize, type],
		queryFn: () => getActions(search, page, pageSize, type),
		staleTime: 5 * 60 * 1000, // 5 minutes
		// Keep the previous page's data while a new search/page loads so the list
		// (and the search box) never unmount into the full-page loading state on
		// each keystroke — that unmount was stealing input focus (SIMS-6516).
		placeholderData: keepPreviousData,
	});
};

export const useGetAllActions = () => {
	return useQuery({
		queryKey: ["actions", "all"],
		queryFn: () => getAllActions(),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

export const useCreateAction = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createAction,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["actions"] });
			// SIMS-9126 — an action's group decides where it sits in the roles preview, so a
			// create, a regroup or a delete must refresh that cache too; otherwise the preview
			// keeps showing the old Ungrouped section for up to its 60 s stale time.
			queryClient.invalidateQueries({
				queryKey: ["action-groups", "roles-preview"],
			});
		},
	});
};

export const useUpdateAction = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateAction,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["actions"] });
			// SIMS-9126 — an action's group decides where it sits in the roles preview, so a
			// create, a regroup or a delete must refresh that cache too; otherwise the preview
			// keeps showing the old Ungrouped section for up to its 60 s stale time.
			queryClient.invalidateQueries({
				queryKey: ["action-groups", "roles-preview"],
			});
		},
	});
};

export const useDeleteAction = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteAction,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["actions"] });
			// SIMS-9126 — an action's group decides where it sits in the roles preview, so a
			// create, a regroup or a delete must refresh that cache too; otherwise the preview
			// keeps showing the old Ungrouped section for up to its 60 s stale time.
			queryClient.invalidateQueries({
				queryKey: ["action-groups", "roles-preview"],
			});
		},
	});
};
